import { useCallback, useEffect, useMemo, useRef } from "react";
import { DEFAULT_SORT } from "constants/pricerTable";
import { storeInLocalStorage } from "helpers/Pricer/Table";
import {
  ACTIONS,
  CATEGORIES,
  COMPONENT_NAMES,
  CONTEXTS,
  DESCRIPTIONS,
  ELEMENTS,
  EVENT_NAMES,
  sendEvent,
} from "helpers/googleAnalyticsHelper";
import { SUMMARY_IDS } from "./constants";
import { ITEM_KEYS } from "./pricerToolbarItems";
import { PRICER_TOOLS } from "./usePricerTools";

const storeSummaryIdsInLocalStorage = (pricerTablesData) => {
  const summaryIds =
    pricerTablesData && pricerTablesData.map((item) => item.summary_id);
  storeInLocalStorage(SUMMARY_IDS, JSON.stringify(summaryIds));
};

/**
 * Click handlers for the Pricer toolbar items: each sends its GA event, runs
 * the grid action or opens its tool, and closes the "Pricer Tools" menu.
 *
 * Returns `handlersByKey` (by toolbar item key) and `openQuickAdd(source)`,
 * which the "/" shortcut also uses.
 */
const usePricerToolbarActions = ({
  pricerRefAgGrid,
  pricerTablesData,
  tools,
  closeCompactMenu,
  onFilterButtonClick,
}) => {
  const { open: openTool } = tools;
  const addOptionOpenTimerRef = useRef(null);
  const isQuickAddOpenRef = useRef(false);
  isQuickAddOpenRef.current = tools.isOpen[PRICER_TOOLS.QUICK_ADD];

  useEffect(
    () => () => {
      if (addOptionOpenTimerRef.current) {
        clearTimeout(addOptionOpenTimerRef.current);
      }
    },
    []
  );

  const handleResetSortClick = useCallback(() => {
    pricerRefAgGrid.columnApi.applyColumnState({
      state: DEFAULT_SORT,
      defaultState: { sort: null },
    });
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_RESET_SORT_BUTTON,
    });
    closeCompactMenu();
  }, [pricerRefAgGrid, closeCompactMenu]);

  const handleCollapseAllClick = useCallback(() => {
    pricerRefAgGrid?.api && pricerRefAgGrid.api.collapseAll();
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      component: COMPONENT_NAMES.BUTTON,
      eventName: EVENT_NAMES.PRICER_COLLAPSE_ALL_BUTTON,
    });
    closeCompactMenu();
  }, [pricerRefAgGrid, closeCompactMenu]);

  const handleCSVImportClick = useCallback(() => {
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      component: COMPONENT_NAMES.BUTTON,
      eventName: EVENT_NAMES.PRICER_CSV_IMPORT_OPEN,
    });
    openTool(PRICER_TOOLS.CSV_IMPORT);
    closeCompactMenu();
  }, [openTool, closeCompactMenu]);

  const openQuickAdd = useCallback(
    (openSource = "button") => {
      // Only count a real open (closed → open). Re-pressing / or the button
      // while open just refocuses the bar.
      if (!isQuickAddOpenRef.current) {
        sendEvent({
          category: CATEGORIES.PRICER,
          context: CONTEXTS.PRICER,
          action: ACTIONS.CLICK,
          component: COMPONENT_NAMES.BUTTON,
          eventName: EVENT_NAMES.PRICER_QUICK_ADD_OPEN,
          extraParams: {
            open_source: openSource,
          },
        });
      }
      openTool(PRICER_TOOLS.QUICK_ADD); // also bumps quickAddFocusKey
      closeCompactMenu();
    },
    [openTool, closeCompactMenu]
  );

  const handleQuickAddClick = useCallback(() => {
    openQuickAdd("button");
  }, [openQuickAdd]);

  const handleImportTradesClick = useCallback(() => {
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      component: COMPONENT_NAMES.BUTTON,
      eventName: EVENT_NAMES.PRICER_IMPORT_TRADES_OPEN,
    });
    openTool(PRICER_TOOLS.TRADE_CAPTURE);
    closeCompactMenu();
  }, [openTool, closeCompactMenu]);

  const handleVolSurfaceComparisonClick = useCallback(() => {
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      component: COMPONENT_NAMES.BUTTON,
      eventName: EVENT_NAMES.PRICER_VOL_SURFACE_COMPARISON_OPEN,
    });
    openTool(PRICER_TOOLS.VOL_COMPARISON);
    closeCompactMenu();
  }, [openTool, closeCompactMenu]);

  const handleAddOptionClick = useCallback(
    (event) => {
      const native = event?.domEvent ?? event;
      native?.stopPropagation?.();
      closeCompactMenu();
      storeSummaryIdsInLocalStorage(pricerTablesData);
      sendEvent({
        category: CATEGORIES.PRICER,
        description: DESCRIPTIONS.ADD_OPTION,
        context: CONTEXTS.PRICER,
        action: ACTIONS.CLICK,
        element: ELEMENTS.ADD_OPTION,
        component: COMPONENT_NAMES.BUTTON,
        eventName: EVENT_NAMES.PRICER_ADD_OPTION_OPEN,
      });
      // Next tick: this click must not land on the modal mask (two-click open).
      if (addOptionOpenTimerRef.current) {
        clearTimeout(addOptionOpenTimerRef.current);
      }
      addOptionOpenTimerRef.current = window.setTimeout(() => {
        addOptionOpenTimerRef.current = null;
        openTool(PRICER_TOOLS.ADD_OPTION);
      }, 0);
    },
    [pricerTablesData, openTool, closeCompactMenu]
  );

  const handlersByKey = useMemo(
    () => ({
      [ITEM_KEYS.COMMODITIES]: onFilterButtonClick,
      [ITEM_KEYS.ADD_OPTION]: handleAddOptionClick,
      [ITEM_KEYS.RESET_SORT]: handleResetSortClick,
      [ITEM_KEYS.DATE]: undefined, // the date picker handles its own change
      [ITEM_KEYS.COLLAPSE_ALL]: handleCollapseAllClick,
      [ITEM_KEYS.CSV_IMPORT]: handleCSVImportClick,
      [ITEM_KEYS.QUICK_ADD]: handleQuickAddClick,
      [ITEM_KEYS.IMPORT_TRADES]: handleImportTradesClick,
      [ITEM_KEYS.VOL_SURFACE_COMPARISON]: handleVolSurfaceComparisonClick,
    }),
    [
      onFilterButtonClick,
      handleAddOptionClick,
      handleResetSortClick,
      handleCollapseAllClick,
      handleCSVImportClick,
      handleQuickAddClick,
      handleImportTradesClick,
      handleVolSurfaceComparisonClick,
    ]
  );

  return { handlersByKey, openQuickAdd };
};

export default usePricerToolbarActions;
